import { useEffect, useMemo, useRef, useState } from 'react';
import { PRIORITY, useKeys } from '../lib/keyboard.js';
import { fuzzyScore } from '../lib/search.js';
import { useT } from '../i18n/index.js';

export interface PickerOption {
  value: string;
  label: string;
  color?: string;
  count?: number;
}

export interface PickerProps {
  title: string;
  options: PickerOption[];
  selected: string[];
  /** Multi: Space toggles and stays open, Enter toggles and closes. Single: Enter/digit picks and closes. */
  multi?: boolean;
  onToggle: (value: string) => void;
  onClose: () => void;
  /** Where to anchor; defaults to the top of the view. */
  anchor?: { x: number; y: number } | null;
}

/**
 * Keyboard menu (§7): options are numbered, typing filters them, ↑/↓ or j/k move,
 * Enter applies, Esc cancels. Pressing the picker's own letter again is handled by the
 * caller (it closes the picker: toggle semantics).
 */
export function Picker({ title, options, selected, multi = false, onToggle, onClose, anchor }: PickerProps) {
  const t = useT();
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const visible = useMemo(() => {
    if (!query) return options;
    return options
      .map((o) => ({ o, s: fuzzyScore(query, o.label) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .map((x) => x.o);
  }, [options, query]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    setCursor(0);
  }, [query]);

  useEffect(() => {
    const el = listRef.current?.children[cursor] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  const pick = (i: number, close: boolean) => {
    const opt = visible[i];
    if (!opt) return;
    onToggle(opt.value);
    if (close || !multi) onClose();
  };

  // The input has focus, so the global layer ignores keys; handle them here.
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || (e.key === 'j' && !query)) {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, visible.length - 1));
    } else if (e.key === 'ArrowUp' || (e.key === 'k' && !query)) {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(cursor, true);
    } else if (e.key === ' ' && multi && !query) {
      e.preventDefault();
      pick(cursor, false);
    } else if (/^[1-9]$/.test(e.key) && !query) {
      e.preventDefault();
      pick(Number(e.key) - 1, true);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  useKeys({ Escape: onClose }, { priority: PRIORITY.picker });

  const style = anchor ? { left: Math.min(anchor.x, window.innerWidth - 300), top: anchor.y } : { left: 16, top: 112 };

  return (
    <>
      <div className="fixed inset-0 z-30" onMouseDown={onClose} />
      <div role="listbox" aria-label={title} className="fixed z-40 w-72 max-w-[calc(100vw-32px)] rounded-xl border border-line bg-paper shadow-xl" style={style}>
        <div className="flex items-center justify-between border-b border-line px-3 py-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted">{title}</span>
          <span className="text-[11px] text-muted">
            {multi ? (
              <>
                <kbd>Space</kbd> {t('picker.toggle')} · <kbd>Enter</kbd> {t('picker.done')}
              </>
            ) : (
              <>
                <kbd>Enter</kbd> {t('picker.pick')}
              </>
            )}
          </span>
        </div>
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t('picker.filter')}
          className="w-full border-b border-line bg-transparent px-3 py-2 text-sm outline-none"
        />
        <div ref={listRef} className="max-h-72 overflow-y-auto py-1">
          {visible.length === 0 && <div className="px-3 py-2 text-sm text-muted">{t('picker.noMatch')}</div>}
          {visible.map((o, i) => {
            const on = selected.includes(o.value);
            return (
              <div
                key={o.value}
                role="option"
                aria-selected={on}
                onMouseEnter={() => setCursor(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(i, !multi);
                }}
                className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm ${i === cursor ? 'bg-accent-soft' : ''}`}
              >
                <span className="w-4 text-right text-[11px] text-muted">{i < 9 && !query ? i + 1 : ''}</span>
                <span className={`flex h-4 w-4 items-center justify-center rounded border text-[10px] ${on ? 'border-accent bg-accent text-white' : 'border-line'}`}>
                  {on ? '✓' : ''}
                </span>
                <span className="tag flex-1" data-color={o.color}>
                  {o.label || t('common.empty')}
                </span>
                {o.count !== undefined && <span className="text-[11px] text-muted">{o.count}</span>}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
